// Story 11.12 (FR56d) — the ONE place the console says what survives an erasure.
//
// Two surfaces make this statement and they used to make it independently: the
// confirmation modal named four things and the post-erase banner named two, so
// the admin was told one story before pressing the button and a shorter one
// after. Both now render from this file, which is the entire point of it — a
// retention that is added, removed or reworded changes here and appears in both
// places, or it changes nowhere.
//
// WHY THIS IS A FRONTEND CONSTANT AND NOT THE SERVER'S RESPONSE. The erase
// endpoint returns `layers_retained` — the same identifiers keying the record
// below — but the copy cannot be generated from it: each sentence carries the
// REASON for its retention ("an AMPS score can only be read together with…"),
// and a reason is not derivable from an identifier. The modal settles it
// anyway: it renders BEFORE the request exists, so it has no response to read.
//
// The server's list is therefore a TRIPWIRE rather than a source. The
// co-located test asserts that the key set here equals the server's
// `LAYERS_RETAINED`, so a fifth retained layer added server-side fails the
// frontend suite until somebody writes its sentence. A layer cannot be added
// quietly on one side and forgotten on the other.

import type { RetentionLayer } from "@/hooks/queries/admin-users-types";

/**
 * The layers erasure does NOT destroy, keyed by the identifiers the server puts
 * on the wire (`LAYERS_RETAINED`, `erase_user_account/command.py`).
 *
 * Taken from the wire types rather than re-declared here: the closed set is one
 * fact, and the response schema is where this client states it. Re-exported
 * because the sentences below are what a reader comes to this file for, and the
 * key they are filed under should be readable in the same place.
 *
 * `billing_record` is the odd one out and is worded accordingly: the other two
 * are platform stores the erasure deliberately does not touch, while this one
 * is held by the payment provider under its own rules. The sentence therefore
 * promises nothing about the provider's copy disappearing, because it will not.
 *
 * It also states what the cancellation does NOT come with. Erasing an account
 * part-way through a paid period buys back none of that period — the client has
 * ruled there is no pro-rata refund — and the admin reading this modal is the
 * last person able to tell the data subject so before the money is spent.
 */
export type { RetentionLayer };

export interface RetentionStatement {
  /** Matches one value of the server's `layers_retained`. */
  layer: RetentionLayer;
  /** One sentence, admin-facing, safe to render on its own. */
  sentence: string;
}

/**
 * What erasure DESTROYS. Not a retention, so it carries no layer key and is not
 * part of the tripwire — it belongs to the modal, which is asked to describe an
 * action that has not happened yet. The banner does not use it: afterwards the
 * console names the person instead ("…'s account … have been erased").
 */
export const ERASURE_DESTRUCTION_SENTENCE =
  "The account and personal details are removed and access ends immediately. This cannot be undone.";

/**
 * The retention sentences, in the order both surfaces read them out.
 *
 * ORDER IS DELIBERATE: the clinical record first, because it is the retention
 * an admin is most likely to be challenged about ("you said you deleted them,
 * why is their name on this evaluation"); then the audit record; then the one
 * the platform does not itself hold.
 *
 * The audit sentence says "recorded against an internal identifier rather than
 * a name" and MUST NOT be softened into "anonymous", "anonymised" or "no longer
 * identifiable". None of those is true: erasure writes an attribution row
 * mapping the user id to a display name, so the actor stays re-identifiable
 * through a deliberate, narrow, database-level path. Under GDPR the accurate
 * word for that is *pseudonymised*, and the difference between the two is legal
 * rather than stylistic — an admin who repeats "anonymised" to a data subject
 * is making a claim the platform cannot stand behind.
 */
export const ERASURE_RETENTION_STATEMENTS: readonly RetentionStatement[] = [
  {
    layer: "clinical_record",
    sentence:
      "Evaluations this clinician performed are kept, and keep their name — an AMPS score can only be read together with the rater whose calibrated severity produced it.",
  },
  {
    layer: "audit_trail",
    sentence:
      "Administrative actions remain in the audit record for seven years, recorded against an internal identifier rather than a name.",
  },
  {
    layer: "billing_record",
    sentence:
      "Any active subscription is cancelled immediately. The unused remainder of an already-paid period is not refunded. Billing records held by our payment provider are kept for as long as financial-record rules require. Their name and email address remain with our payment processor alongside the invoices we are required to keep.",
  },
];

/** The key set the tripwire test compares against the server's enumeration. */
export const ERASURE_RETENTION_LAYERS: readonly RetentionLayer[] =
  ERASURE_RETENTION_STATEMENTS.map((statement) => statement.layer);
